import { BACKEND_URL } from "../config";
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MLLearningProgress from './MLLearningProgress';
import ActiveNotifications from './ActiveNotifications';
import UserV4Folder from './v4/UserV4Folder';
import { MyTradesCard } from './performance/MyTradesCard';
import { V4ProtocolPerformance } from './performance/V4ProtocolPerformance';
import { WeeklySummaryCard } from './performance/WeeklySummaryCard';
import { BlindfoldedGuide } from './guide/BlindfoldedGuide';

const UserProfileDashboard = () => {
  const [searchParams] = useSearchParams();
  
  // Read tab from URL query parameter, default to 'notifications'
  // This enables deep linking from push notifications
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['notifications', 'v4folder', 'performance', 'account', 'subscribe', 'invites', 'friends', 'codes', 'mlprogress'];
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      return tabFromUrl;
    }
    return 'notifications';  // Default to Active Notifications (Feb 21, 2026)
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedFriends, setInvitedFriends] = useState([]);
  const [discountCodes, setDiscountCodes] = useState({ available_codes: [], used_codes: [] });
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [tradeStats, setTradeStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [expandedFriendIndex, setExpandedFriendIndex] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundMessage, setRefundMessage] = useState('');
  const [showStrategyGuide, setShowStrategyGuide] = useState(false);
  
  // Trade Performance State (Feb 5, 2026 - Simplified Feb 21, 2026)
  const [performanceData, setPerformanceData] = useState(null);
  const [trackedTrades, setTrackedTrades] = useState([]);
  const [breakoutAlerts, setBreakoutAlerts] = useState([]);
  const [currentPrices, setCurrentPrices] = useState({});
  const [currencyStrength, setCurrencyStrength] = useState(null);
  const [divergenceAlerts, setDivergenceAlerts] = useState([]);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  
  // Performance Query State (Feb 21, 2026)
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customPerformance, setCustomPerformance] = useState(null);
  const [loadingCustomPerformance, setLoadingCustomPerformance] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════════════
  // FRESHNESS & ENTRY QUALITY HELPERS (Feb 12, 2026)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Calculate alert freshness based on creation time
  const getAlertFreshness = (createdAt) => {
    if (!createdAt) return { label: 'UNKNOWN', color: '#6b7280', bgColor: '#374151', hoursOld: 0 };
    
    const created = new Date(createdAt);
    const now = new Date();
    const hoursOld = (now - created) / (1000 * 60 * 60);
    
    if (hoursOld < 1) {
      return { label: '🟢 FRESH', color: '#22c55e', bgColor: '#dcfce7', hoursOld: hoursOld.toFixed(1), message: 'Optimal entry window' };
    } else if (hoursOld < 2) {
      return { label: '🟡 RECENT', color: '#eab308', bgColor: '#fef9c3', hoursOld: hoursOld.toFixed(1), message: 'Good entry window' };
    } else if (hoursOld < 3) {
      return { label: '🟠 AGING', color: '#f97316', bgColor: '#ffedd5', hoursOld: hoursOld.toFixed(1), message: 'Verify before entry' };
    } else {
      return { label: '🔴 STALE', color: '#ef4444', bgColor: '#fee2e2', hoursOld: hoursOld.toFixed(1), message: 'Entry window likely passed' };
    }
  };

  // Calculate entry quality based on current price vs alert entry
  const getEntryQuality = (alert, currentPrice) => {
    if (!alert || !currentPrice || !alert.recommended_entry) {
      return { status: 'UNKNOWN', message: 'Price data unavailable', color: '#6b7280', pipsDiff: 0 };
    }
    
    const entry = alert.recommended_entry;
    const direction = alert.direction;
    const pipMultiplier = alert.pair?.includes('JPY') ? 100 : 10000;
    const pipsDiff = (currentPrice - entry) * pipMultiplier;
    const absPips = Math.abs(pipsDiff);
    
    // For BUY: positive pipsDiff means price moved up (bad - missed entry)
    // For SELL: negative pipsDiff means price moved down (bad - missed entry)
    const movedInDirection = (direction === 'BUY' && pipsDiff > 0) || (direction === 'SELL' && pipsDiff < 0);
    const movedAgainstDirection = (direction === 'BUY' && pipsDiff < 0) || (direction === 'SELL' && pipsDiff > 0);
    
    if (absPips < 10) {
      return { 
        status: 'OPTIMAL', 
        message: '✅ AT ENTRY - Optimal entry', 
        color: '#22c55e',
        bgColor: '#dcfce7',
        pipsDiff: pipsDiff.toFixed(1)
      };
    } else if (movedAgainstDirection && absPips < 30) {
      return { 
        status: 'BETTER', 
        message: `✅ BETTER ENTRY - Price pulled back ${absPips.toFixed(0)} pips`, 
        color: '#22c55e',
        bgColor: '#dcfce7',
        pipsDiff: pipsDiff.toFixed(1)
      };
    } else if (movedAgainstDirection && absPips >= 30) {
      return { 
        status: 'PULLBACK', 
        message: `⚠️ DEEP PULLBACK - ${absPips.toFixed(0)} pips against (verify SL)`, 
        color: '#f97316',
        bgColor: '#ffedd5',
        pipsDiff: pipsDiff.toFixed(1)
      };
    } else if (movedInDirection && absPips < 25) {
      return { 
        status: 'REDUCED', 
        message: `⚠️ MOVED +${absPips.toFixed(0)} pips - Reduced R:R`, 
        color: '#eab308',
        bgColor: '#fef9c3',
        pipsDiff: pipsDiff.toFixed(1)
      };
    } else if (movedInDirection && absPips < 50) {
      return { 
        status: 'LATE', 
        message: `🟠 LATE ENTRY - Already +${absPips.toFixed(0)} pips`, 
        color: '#f97316',
        bgColor: '#ffedd5',
        pipsDiff: pipsDiff.toFixed(1)
      };
    } else {
      return { 
        status: 'MISSED', 
        message: `🔴 ENTRY PASSED - +${absPips.toFixed(0)} pips moved`, 
        color: '#ef4444',
        bgColor: '#fee2e2',
        pipsDiff: pipsDiff.toFixed(1)
      };
    }
  };

  // 🔒 PRODUCTION ONLY - No localhost fallback
  const backendUrl = BACKEND_URL;

  // Handle URL parameter changes (deep linking from push notifications)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['notifications', 'v4folder', 'performance', 'account', 'subscribe', 'invites', 'friends', 'codes', 'mlprogress'];
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchAvailablePlans();
    fetchDiscountCodes();
    fetchInvitedFriends();
    fetchTradeStats();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/business/subscription/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('Subscription info fetch failed:', response.status);
        return;
      }
      const data = await response.json();
      // Only store a valid subscription payload — an error/empty object without
      // a `status` field must NOT be set, or the banner crashes on .toUpperCase().
      if (data && typeof data.status === 'string') {
        setSubscriptionInfo(data);
      }
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/subscription/available-plans`);
      const data = await response.json();
      setAvailablePlans(data.available_plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/referrals/my-codes`);
      const data = await response.json();
      setDiscountCodes(data);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    }
  };

  const fetchInvitedFriends = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/business/invites/my-invites`);
      const data = await response.json();
      setInvitedFriends(data.invited_friends || []);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  };

  const fetchTradeStats = async () => {
    try {
      setLoadingStats(true);
      const response = await fetch(`${backendUrl}/api/trade-stats`);
      const data = await response.json();
      if (data.success) {
        setTradeStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching trade stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Fetch user's performance data (Feb 5, 2026)
  const fetchPerformanceData = async () => {
    setLoadingPerformance(true);
    const token = localStorage.getItem('auth_token');
    
    // Fetch each data source independently so one failure doesn't block others
    
    // 1. Fetch tracked trades for this user
    try {
      const tradesResponse = await fetch(`${backendUrl}/api/trades`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const tradesData = await tradesResponse.json();
      if (Array.isArray(tradesData)) {
        setTrackedTrades(tradesData);
      } else if (tradesData.trades) {
        setTrackedTrades(tradesData.trades);
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    }

    // 2. Fetch active breakout alerts
    try {
      const alertsResponse = await fetch(`${backendUrl}/api/breakout-alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const alertsData = await alertsResponse.json();
      if (alertsData.alerts) {
        const activeAlerts = alertsData.alerts.filter(a => !a.expired && !a.dismissed);
        setBreakoutAlerts(activeAlerts);
        
        // Fetch current prices for all alert pairs (for entry quality comparison)
        const pairs = [...new Set(activeAlerts.map(a => a.pair))];
        const pricesObj = {};
        for (const pair of pairs) {
          try {
            const priceRes = await fetch(`${backendUrl}/api/price/${pair}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const priceData = await priceRes.json();
            if (priceData.price) {
              pricesObj[pair] = priceData.price;
            }
          } catch (e) {
            console.error(`Error fetching price for ${pair}:`, e);
          }
        }
        setCurrentPrices(pricesObj);
      }
    } catch (error) {
      console.error('Error fetching breakout alerts:', error);
    }

    // 3. Fetch currency strength
    try {
      const strengthResponse = await fetch(`${backendUrl}/api/currency-strength/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const strengthData = await strengthResponse.json();
      if (strengthData.strengths) {
        setCurrencyStrength(strengthData);
      }
    } catch (error) {
      console.error('Error fetching currency strength:', error);
    }

    // 4. Fetch divergence alerts
    try {
      const divergenceResponse = await fetch(`${backendUrl}/api/divergence-alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const divergenceData = await divergenceResponse.json();
      if (divergenceData.alerts) {
        setDivergenceAlerts(divergenceData.alerts.filter(a => a.status === 'ACTIVE'));
      }
    } catch (error) {
      console.error('Error fetching divergence alerts:', error);
    }

    setLoadingPerformance(false);
  };

  // Performance Query Functions (Feb 21, 2026)
  // 📅 Performance Query — now sourced from the 🏆 Winning folder (protocol performance:
  // real P&L + win rate). Dates optional; when omitted it returns the all-time folder.
  const fetchCustomPerformance = async (startArg, endArg) => {
    setLoadingCustomPerformance(true);
    try {
      const token = localStorage.getItem('auth_token');
      const s = typeof startArg === 'string' ? startArg : customStartDate;
      const e = typeof endArg === 'string' ? endArg : customEndDate;
      const body = (s && e)
        ? { start_date: s, end_date: e }
        : {};
      const response = await fetch(`${backendUrl}/api/notification-lab/performance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        setCustomPerformance(data);
      } else {
        alert('Failed to fetch performance data');
      }
    } catch (error) {
      console.error('Error fetching custom performance:', error);
      alert('Error fetching performance data');
    } finally {
      setLoadingCustomPerformance(false);
    }
  };

  const setQuickDateRange = (days) => {
    // Adelaide calendar days, inclusive of today (7 Days = today + previous 6)
    const ymd = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Australia/Adelaide' });
    const end = new Date();
    const start = new Date(end.getTime() - (days - 1) * 86400000);
    
    const startStr = ymd(start);
    const endStr = ymd(end);
    setCustomStartDate(startStr);
    setCustomEndDate(endStr);
    // Immediately show win rate + P&L for the selected period
    fetchCustomPerformance(startStr, endStr);
  };

  // Fetch performance on tab change
  useEffect(() => {
    if (activeTab === 'performance') {
      fetchPerformanceData();
      fetchCustomPerformance();
    }
  }, [activeTab]);


  const handleSendInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${backendUrl}/api/business/invites/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_email: inviteEmail })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('✅ Invite sent successfully!');
        setInviteEmail('');
        fetchInvitedFriends();
      } else {
        setMessage('❌ Failed to send invite');
      }
    } catch (error) {
      setMessage('❌ Error sending invite');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const response = await fetch(`${backendUrl}/api/business/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planId,
          discount_code: selectedDiscountCode,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
    }
  };

  const handleAddDiscountCode = () => {
    if (discountCodes.available_codes.length > 0) {
      setSelectedDiscountCode(discountCodes.available_codes[0].code);
      setMessage(`✅ Discount code ${discountCodes.available_codes[0].code} applied!`);
    } else {
      setMessage('❌ No available discount codes. Invite friends to earn codes!');
    }
  };

  const handleRequestRefund = async () => {
    setRefundProcessing(true);
    setRefundMessage('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/subscription/request-refund`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRefundMessage(`✅ ${data.message}`);
        setTimeout(() => {
          setShowRefundModal(false);
          fetchSubscriptionInfo(); // Refresh subscription info
        }, 3000);
      } else {
        setRefundMessage(`❌ ${data.detail || 'Refund request failed'}`);
      }
    } catch (error) {
      console.error('Error requesting refund:', error);
      setRefundMessage('❌ Failed to process refund. Please contact support.');
    } finally {
      setRefundProcessing(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h1 style={{ color: 'white', fontSize: '2rem', fontWeight: '700' }}>
            👤 My Profile
          </h1>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <Link to="/calculator" style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              📊 Trade
            </Link>
            <button
              onClick={() => {
                localStorage.removeItem('auth_token');
                window.location.href = '/';
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(239, 68, 68, 0.9)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                border: '2px solid rgba(255,255,255,0.3)',
                cursor: 'pointer'
              }}
            >
              🚪 Logout
            </button>
          </div>
          
          {/* How to Trade Blindfolded Button */}
          <button
            onClick={() => setShowStrategyGuide(true)}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              color: '#1a1a2e',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '700',
              border: '2px solid rgba(255,255,255,0.4)',
              cursor: 'pointer',
              fontSize: '1rem',
              boxShadow: '0 4px 15px rgba(251, 191, 36, 0.4)',
              width: '100%'
            }}
          >
            📖 How to Trade Blindfolded
          </button>
        </div>

        {/* Subscription Status Banner */}
        {subscriptionInfo && subscriptionInfo.status && (
          <div style={{
            background: subscriptionInfo.status === 'trial' 
              ? 'rgba(251, 191, 36, 0.9)' 
              : subscriptionInfo.status === 'expired'
              ? 'rgba(239, 68, 68, 0.9)'
              : 'rgba(34, 197, 94, 0.9)',
            padding: '1rem',
            borderRadius: '10px',
            marginBottom: '2rem',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{subscriptionInfo.status?.toUpperCase()}</strong> - {subscriptionInfo.days_remaining} days remaining
                ({subscriptionInfo.current_tier} tier)
              </div>
              {subscriptionInfo.status === 'expired' && (
                <button
                  onClick={() => setActiveTab('subscribe')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'white',
                    color: '#ef4444',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Renew Now
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          {['notifications', 'v4folder', 'performance', 'account', 'subscribe', 'invites', 'friends', 'codes', 'mlprogress'].map((tab) => {
            // Only these tabs are unlocked (Feb 21, 2026)
            const unlockedTabs = ['notifications', 'v4folder', 'performance'];
            const isLocked = !unlockedTabs.includes(tab);
            
            return (
              <button
                key={tab}
                data-testid={`tab-${tab}`}
                onClick={() => !isLocked && setActiveTab(tab)}
                disabled={isLocked}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: isLocked 
                    ? 'rgba(100, 116, 139, 0.3)' 
                    : activeTab === tab 
                      ? 'white' 
                      : 'rgba(255, 255, 255, 0.2)',
                  color: isLocked 
                    ? 'rgba(255, 255, 255, 0.4)' 
                    : activeTab === tab 
                      ? '#0891b2' 
                      : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  opacity: isLocked ? 0.5 : 1,
                  position: 'relative'
                }}
                title={isLocked ? '🔒 Coming Soon' : ''}
              >
                {tab === 'notifications' && '📱 Active Alerts'}
                {tab === 'v4folder' && 'V4 Folder'}
                {tab === 'performance' && '📈 Trade Performance'}
                {tab === 'account' && '🔒 My MT5'}
                {tab === 'subscribe' && '🔒 Subscribe'}
                {tab === 'invites' && '🔒 Invite Friends'}
                {tab === 'friends' && '🔒 My Friends'}
                {tab === 'codes' && '🔒 Discount Codes'}
                {tab === 'mlprogress' && '🔒 ML Progress'}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Active Notifications Tab (Feb 21, 2026) - DEFAULT */}
          {activeTab === 'notifications' && (
            <>
              <WeeklySummaryCard />
              <ActiveNotifications />
            </>
          )}

          {activeTab === 'v4folder' && (
            <UserV4Folder />
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>💹 My MT5 Account</h2>
              
              {!subscriptionInfo ? (
                <div style={{
                  padding: '3rem',
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  color: '#64748b',
                  marginBottom: '2rem'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                  <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Loading your account information...</div>
                </div>
              ) : (
                <div>
              
              {/* Active Subscription Card */}
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                border: '2px solid #0891b2'
              }}>
                <h3 style={{ fontSize: '1.25rem', color: '#0891b2', marginBottom: '1rem' }}>
                  Active Subscription
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#0369a1', marginBottom: '0.25rem' }}>Status</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0c4a6e' }}>
                      {subscriptionInfo.status?.toUpperCase()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#0369a1', marginBottom: '0.25rem' }}>Plan</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0c4a6e' }}>
                      {subscriptionInfo.plan ? subscriptionInfo.plan.replace('_', ' ').toUpperCase() : 'Trial'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#0369a1', marginBottom: '0.25rem' }}>Current Tier</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0c4a6e' }}>
                      {subscriptionInfo.current_tier?.toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Countdown Timers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Subscription Expiry Countdown */}
                <div style={{
                  padding: '1.5rem',
                  background: subscriptionInfo.days_remaining <= 3 
                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                    : subscriptionInfo.days_remaining <= 7
                    ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                    : 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                  borderRadius: '12px',
                  border: `2px solid ${
                    subscriptionInfo.days_remaining <= 3 ? '#ef4444'
                    : subscriptionInfo.days_remaining <= 7 ? '#fbbf24'
                    : '#22c55e'
                  }`,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Subscription Expires In
                  </div>
                  <div style={{
                    fontSize: '3rem',
                    fontWeight: '700',
                    color: subscriptionInfo.days_remaining <= 3 ? '#dc2626'
                      : subscriptionInfo.days_remaining <= 7 ? '#f59e0b'
                      : '#16a34a',
                    marginBottom: '0.25rem'
                  }}>
                    {subscriptionInfo.days_remaining}
                  </div>
                  <div style={{ fontSize: '1rem', color: '#64748b', fontWeight: '600' }}>
                    {subscriptionInfo.days_remaining === 1 ? 'Day' : 'Days'}
                  </div>
                  {subscriptionInfo.days_remaining <= 7 && (
                    <button
                      onClick={() => setActiveTab('subscribe')}
                      style={{
                        marginTop: '1rem',
                        padding: '0.5rem 1rem',
                        background: '#0891b2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Renew Now
                    </button>
                  )}
                </div>

                {/* Tier 1 Countdown (only show if days_until_tier2 > 0) */}
                {subscriptionInfo.days_until_tier2 && subscriptionInfo.days_until_tier2 > 0 && (
                  <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderRadius: '12px',
                    border: '2px solid #fbbf24',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#78350f', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Tier 1 Pricing Available For
                    </div>
                    <div style={{ fontSize: '3rem', fontWeight: '700', color: '#f59e0b', marginBottom: '0.25rem' }}>
                      {subscriptionInfo.days_until_tier2}
                    </div>
                    <div style={{ fontSize: '1rem', color: '#78350f', fontWeight: '600' }}>
                      {subscriptionInfo.days_until_tier2 === 1 ? 'Day' : 'Days'}
                    </div>
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      color: '#78350f'
                    }}>
                      After 60 days from first purchase, pricing switches to Tier 2 ($500-$1300)
                    </div>
                  </div>
                )}
              </div>

              {/* Account Details */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Account Details</h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Member Since:</span>
                    <strong>{new Date(subscriptionInfo.registration_date).toLocaleDateString()}</strong>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Days Since Registration:</span>
                    <strong>{subscriptionInfo.days_since_registration} days</strong>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Available Discount Codes:</span>
                    <strong style={{ color: '#16a34a' }}>{discountCodes.available_codes.length}</strong>
                  </div>
                  <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Friends Invited:</span>
                    <strong>{invitedFriends.length}</strong>
                  </div>
                </div>
              </div>

              {/* Trading Statistics Section */}
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                border: '2px solid #fbbf24'
              }}>
                <h3 style={{ fontSize: '1.25rem', color: '#78350f', marginBottom: '1rem' }}>
                  📊 30-Day Trading Statistics
                </h3>
                
                {loadingStats ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#78350f' }}>
                    Loading statistics...
                  </div>
                ) : tradeStats && tradeStats.total_trades > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    {/* Total Trades */}
                    <StatBox label="Total Trades" value={tradeStats.total_trades} />
                    <StatBox label="Win Rate" value={`${tradeStats.win_rate.toFixed(1)}%`} valueColor={tradeStats.win_rate >= 50 ? '#16a34a' : '#dc2626'} />
                    <StatBox label="Net Profit" value={`$${tradeStats.net_profit.toFixed(2)}`} valueColor={tradeStats.net_profit >= 0 ? '#16a34a' : '#dc2626'} />
                    
                    {/* Profit/Loss Stats */}
                    <StatBox label="Profit Trades" value={tradeStats.profit_trades} valueColor="#16a34a" />
                    <StatBox label="Loss Trades" value={tradeStats.loss_trades} valueColor="#dc2626" />
                    <StatBox label="Profit Factor" value={tradeStats.profit_factor.toFixed(2)} />
                    
                    {/* Trade Distribution */}
                    <StatBox label="Long Trades" value={tradeStats.long_trades} />
                    <StatBox label="Short Trades" value={tradeStats.short_trades} />
                    <StatBox label="Avg Trade Size" value={tradeStats.avg_trade_size.toFixed(2)} />
                    
                    {/* Financial Metrics */}
                    <StatBox label="Gross Profit" value={`$${tradeStats.gross_profit.toFixed(2)}`} valueColor="#16a34a" />
                    <StatBox label="Gross Loss" value={`$${tradeStats.gross_loss.toFixed(2)}`} valueColor="#dc2626" />
                    <StatBox label="Best Profit" value={`$${tradeStats.best_profit.toFixed(2)}`} valueColor="#16a34a" />
                    
                    {/* Averages */}
                    <StatBox label="Avg Profit" value={`$${tradeStats.avg_profit.toFixed(2)}`} />
                    <StatBox label="Avg Loss" value={`$${tradeStats.avg_loss.toFixed(2)}`} />
                    <StatBox label="Expectancy" value={`$${tradeStats.expectancy.toFixed(2)}`} valueColor={tradeStats.expectancy >= 0 ? '#16a34a' : '#dc2626'} />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📈</div>
                    <div style={{ color: '#78350f', fontSize: '1rem', marginBottom: '0.5rem' }}>No trading data yet</div>
                    <div style={{ color: '#92400e', fontSize: '0.875rem' }}>Start trading to see your statistics here</div>
                  </div>
                )}
              </div>
              </div>
              )}
            </div>
          )}

          {/* Trade Performance Tab - Simplified Feb 21, 2026 */}
          {activeTab === 'performance' && (
            <div data-testid="performance-tab">
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0891b2' }}>
                📈 My Trade Performance
              </h2>

              <MyTradesCard period={customPerformance?.period} />

              {/* Performance Query Section */}
              <div style={{
                padding: '1.5rem',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '2px solid #e2e8f0',
                marginBottom: '2rem'
              }}>
                <h3 style={{ fontSize: '1.25rem', color: '#0c4a6e', marginBottom: '1rem' }}>
                  📅 Performance Query
                </h3>
                
                {/* Quick Select Buttons */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Quick Select:
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button 
                      data-testid="quick-select-7"
                      onClick={() => setQuickDateRange(7)} 
                      style={{
                        padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                        borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                      }}>7 Days</button>
                    <button 
                      data-testid="quick-select-30"
                      onClick={() => setQuickDateRange(30)} 
                      style={{
                        padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                        borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                      }}>30 Days</button>
                    <button 
                      data-testid="quick-select-90"
                      onClick={() => setQuickDateRange(90)} 
                      style={{
                        padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                        borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                      }}>90 Days</button>
                    <button 
                      data-testid="quick-select-365"
                      onClick={() => setQuickDateRange(365)} 
                      style={{
                        padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                        borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                      }}>365 Days</button>
                  </div>
                </div>

                {/* Date Pickers */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '120px' }}>
                    <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                      From:
                    </label>
                    <input
                      type="date"
                      data-testid="date-from"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '120px' }}>
                    <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                      To:
                    </label>
                    <input
                      type="date"
                      data-testid="date-to"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <button
                    data-testid="fetch-performance-btn"
                    onClick={fetchCustomPerformance}
                    disabled={loadingCustomPerformance}
                    style={{
                      padding: '0.75rem 2rem',
                      background: loadingCustomPerformance ? '#94a3b8' : '#0891b2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: loadingCustomPerformance ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {loadingCustomPerformance ? 'Loading...' : 'Fetch Performance'}
                  </button>
                </div>
              </div>

              <V4ProtocolPerformance />
            </div>
          )}

          {/* Subscribe Tab */}
          {activeTab === 'subscribe' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                {subscriptionInfo?.status === 'expired' ? 'Renew Subscription' : 'Upgrade Subscription'}
              </h2>

              {/* Discount Code Section */}
              {discountCodes.available_codes.length > 0 && (
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #dcfce7 0%, #86efac 100%)',
                  borderRadius: '12px',
                  marginBottom: '2rem',
                  border: '2px solid #16a34a'
                }}>
                  <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#16a34a' }}>
                    🎁 You have {discountCodes.available_codes.length} discount code(s) available!
                  </h3>
                  <button
                    onClick={handleAddDiscountCode}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '1rem'
                    }}
                  >
                    Add Discount Code (50% OFF)
                  </button>
                  {selectedDiscountCode && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: 'white',
                      borderRadius: '8px',
                      fontWeight: '700',
                      color: '#16a34a',
                      fontSize: '1.125rem',
                      letterSpacing: '2px'
                    }}>
                      Applied: {selectedDiscountCode}
                    </div>
                  )}
                </div>
              )}

              {message && (
                <div style={{
                  padding: '1rem',
                  background: '#f0f0f0',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  {message}
                </div>
              )}

              {/* Pricing Plans */}
              <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                {availablePlans.map((plan) => {
                  const originalPrice = plan.price;
                  const discountedPrice = selectedDiscountCode ? originalPrice * 0.5 : originalPrice;
                  const hasDiscount = selectedDiscountCode !== null;

                  return (
                    <div key={plan.plan_id} style={{
                      padding: '1.5rem',
                      background: plan.tier === 'demo' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                      border: `2px solid ${plan.tier === 'demo' ? '#3b82f6' : '#eab308'}`,
                      borderRadius: '12px',
                      position: 'relative'
                    }}>
                      {hasDiscount && (
                        <div style={{
                          position: 'absolute',
                          top: '-10px',
                          right: '10px',
                          background: '#16a34a',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '700'
                        }}>
                          50% OFF
                        </div>
                      )}
                      <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>{plan.name}</h3>
                      <div style={{ marginBottom: '1rem' }}>
                        {hasDiscount && (
                          <div style={{
                            fontSize: '1.25rem',
                            color: '#94a3b8',
                            textDecoration: 'line-through'
                          }}>
                            ${originalPrice}
                          </div>
                        )}
                        <div style={{
                          fontSize: '2rem',
                          fontWeight: '700',
                          color: plan.tier === 'demo' ? '#3b82f6' : '#eab308'
                        }}>
                          ${discountedPrice.toFixed(2)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUpgrade(plan.plan_id)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: plan.tier === 'demo' ? '#3b82f6' : '#eab308',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Subscribe Now
                      </button>
                    </div>
                  );
                })}
              </div>

              <div style={{
                marginTop: '2rem',
                padding: '1rem',
                background: '#fef3c7',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: '#78350f'
              }}>
                <strong>Note:</strong> Subscriptions do not auto-renew. You'll need to manually purchase a new subscription when it expires.
              </div>

              {/* Refund Button - Only show for active Tier 1 subscribers */}
              {subscriptionInfo?.status === 'active' && subscriptionInfo?.current_tier === 'tier1' && !subscriptionInfo?.refund_requested && (
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px solid #ef4444',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#dc2626' }}>
                    💔 Cancel Subscription & Request Refund
                  </h3>
                  <p style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#991b1b' }}>
                    You can request a full refund. You will retain access until your subscription period ends.
                  </p>
                  <button
                    onClick={() => setShowRefundModal(true)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '1rem'
                    }}
                  >
                    Request Full Refund
                  </button>
                </div>
              )}

              {/* Refund Modal */}
              {showRefundModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999,
                  padding: '1rem'
                }}>
                  <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '2rem',
                    maxWidth: '500px',
                    width: '100%',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
                  }}>
                    <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#dc2626' }}>
                      ⚠️ Confirm Refund Request
                    </h2>
                    <div style={{
                      padding: '1rem',
                      background: '#fef2f2',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: '2px solid #fecaca'
                    }}>
                      <p style={{ marginBottom: '1rem', fontSize: '0.95rem', lineHeight: '1.6' }}>
                        <strong>Important Warning:</strong>
                      </p>
                      <p style={{ marginBottom: '1rem', fontSize: '0.875rem', lineHeight: '1.6', color: '#991b1b' }}>
                        If you proceed with a refund, you will get <strong>full access to tradeblindfolded.com until your subscription period ends</strong>, but you will <strong>lose access to your account</strong> and you will be required to <strong>go through the request an invite process again</strong>, and it <strong>may not get accepted</strong>.
                      </p>
                      <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b' }}>
                        Your email and account details will be blacklisted.
                      </p>
                    </div>

                    {refundMessage && (
                      <div style={{
                        padding: '1rem',
                        background: refundMessage.startsWith('✅') ? '#dcfce7' : '#fee2e2',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        color: refundMessage.startsWith('✅') ? '#16a34a' : '#dc2626'
                      }}>
                        {refundMessage}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setShowRefundModal(false);
                          setRefundMessage('');
                        }}
                        disabled={refundProcessing}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: '#e5e7eb',
                          color: '#374151',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: refundProcessing ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          opacity: refundProcessing ? 0.5 : 1
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRequestRefund}
                        disabled={refundProcessing}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: refundProcessing ? '#9ca3af' : '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: refundProcessing ? 'not-allowed' : 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        {refundProcessing ? 'Processing...' : 'Yes, Process Refund'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Invite Friends Tab */}
          {activeTab === 'invites' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Invite Friends & Earn Discounts</h2>
              <div style={{
                padding: '1rem',
                background: '#dcfce7',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <strong style={{ color: '#16a34a' }}>Earn 50% OFF:</strong> When your friend purchases their first subscription, you'll receive a 50% discount code for your next purchase!
              </div>
              <form onSubmit={handleSendInvite} style={{ maxWidth: '500px' }}>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="friend@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    marginBottom: '1rem'
                  }}
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '0.75rem 2rem',
                    background: '#0891b2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </form>
              {message && !selectedDiscountCode && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f0f0', borderRadius: '8px' }}>
                  {message}
                </div>
              )}
            </div>
          )}

          {/* My Friends Tab */}
          {activeTab === 'friends' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>
                My Invited Friends ({invitedFriends.length})
              </h2>
              
              {invitedFriends.length === 0 ? (
                <div style={{
                  padding: '3rem',
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                  <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No friends invited yet</div>
                  <div style={{ fontSize: '0.875rem' }}>Go to the "Invite Friends" tab to send invites</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {invitedFriends.map((friend, index) => (
                    <div key={index} style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      background: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      {/* Friend Name/Email Header - Clickable */}
                      <div
                        onClick={() => setExpandedFriendIndex(expandedFriendIndex === index ? null : index)}
                        style={{
                          padding: '1.25rem',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: expandedFriendIndex === index ? '#f0f9ff' : 'white',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (expandedFriendIndex !== index) e.target.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (expandedFriendIndex !== index) e.target.style.background = 'white';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '1.125rem'
                          }}>
                            {friend.email.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0c4a6e' }}>
                              {friend.email}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                              <span style={{
                                padding: '0.15rem 0.5rem',
                                borderRadius: '8px',
                                background: friend.registered ? '#dcfce7' : '#fee2e2',
                                color: friend.registered ? '#16a34a' : '#dc2626',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {friend.registered ? 'Registered' : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: '1.5rem',
                          color: '#64748b',
                          transition: 'transform 0.3s ease',
                          transform: expandedFriendIndex === index ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                          ▼
                        </div>
                      </div>

                      {/* Expanded Details Section */}
                      {expandedFriendIndex === index && (
                        <div style={{
                          padding: '1.5rem',
                          background: '#f8fafc',
                          borderTop: '1px solid #e2e8f0'
                        }}>
                          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#0369a1', marginBottom: '1rem' }}>
                            Friend Information & Stats
                          </h3>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                            {/* Account Status */}
                            <div style={{
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Account Status
                              </div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0c4a6e' }}>
                                {friend.subscription_status || 'Trial'}
                              </div>
                            </div>

                            {/* Registration Status */}
                            <div style={{
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Registration
                              </div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: friend.registered ? '#16a34a' : '#dc2626' }}>
                                {friend.registered ? 'Complete' : 'Pending'}
                              </div>
                            </div>

                            {/* Purchase Status */}
                            <div style={{
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Purchased Subscription
                              </div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: friend.converted_to_paid ? '#16a34a' : '#94a3b8' }}>
                                {friend.converted_to_paid ? 'Yes ✅' : 'No'}
                              </div>
                            </div>

                            {/* Total Trades */}
                            <div style={{
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Total Trades
                              </div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0c4a6e' }}>
                                {friend.total_trades || 0}
                              </div>
                            </div>

                            {/* Win Rate */}
                            <div style={{
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Win Rate
                              </div>
                              <div style={{
                                fontSize: '1.25rem',
                                fontWeight: '700',
                                color: friend.win_rate && friend.win_rate >= 50 ? '#16a34a' : '#94a3b8'
                              }}>
                                {friend.win_rate ? `${friend.win_rate.toFixed(1)}%` : 'N/A'}
                              </div>
                            </div>

                            {/* Invited At */}
                            <div style={{
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                                Invited On
                              </div>
                              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#0c4a6e' }}>
                                {new Date(friend.invited_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>

                          {/* Reward Status */}
                          {friend.converted_to_paid && (
                            <div style={{
                              marginTop: '1rem',
                              padding: '1rem',
                              background: 'linear-gradient(135deg, #dcfce7 0%, #86efac 100%)',
                              borderRadius: '8px',
                              border: '2px solid #16a34a',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '0.875rem', color: '#15803d', fontWeight: '600' }}>
                                🎉 You earned a 50% discount code from this referral!
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Discount Codes Tab */}
          {activeTab === 'codes' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                My Discount Codes
              </h2>
              
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #dcfce7 0%, #86efac 100%)',
                borderRadius: '12px',
                marginBottom: '2rem',
                border: '2px solid #16a34a',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#16a34a', marginBottom: '0.5rem' }}>
                  {discountCodes.available_codes.length}
                </div>
                <div style={{ fontSize: '1.125rem', color: '#15803d', fontWeight: '600' }}>
                  Available 50% OFF Codes
                </div>
              </div>

              {discountCodes.available_codes.length === 0 ? (
                <div style={{
                  padding: '2rem',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: '#64748b'
                }}>
                  <p style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>No discount codes yet</p>
                  <p>Invite friends to earn 50% OFF codes when they purchase their first subscription!</p>
                </div>
              ) : (
                <div>
                  <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>Code Details:</h3>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {invitedFriends
                      .filter(friend => friend.converted_to_paid && discountCodes.available_codes.some(code => !code.used))
                      .map((friend, index) => (
                        <div key={index} style={{
                          padding: '1rem',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>
                              Earned from:
                            </div>
                            <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0f172a' }}>
                              {friend.email}
                            </div>
                          </div>
                          <div style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, #dcfce7 0%, #86efac 100%)',
                            borderRadius: '8px',
                            fontWeight: '700',
                            color: '#16a34a',
                            fontSize: '0.875rem'
                          }}>
                            50% OFF
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ML Learning Progress Tab */}
          {activeTab === 'mlprogress' && (
            <MLLearningProgress />
          )}
        </div>
      </div>
      
      {showStrategyGuide && <BlindfoldedGuide onClose={() => setShowStrategyGuide(false)} />}
    </div>
  );
};


// Helper component for stat boxes
const StatBox = ({ label, value, valueColor = '#0c4a6e' }) => (
  <div style={{
    padding: '1rem',
    background: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '8px',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '0.5rem', fontWeight: '600' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: valueColor }}>
      {value}
    </div>
  </div>
);

export default UserProfileDashboard;
